import {useState} from "react";
import {
    Alert,
    Select,
    Skeleton,
    Stack,
    Text,
    UnstyledButton,
} from "@mantine/core";
import {useMediaQuery} from "@mantine/hooks";
import {useTranslation} from "react-i18next";
import {format} from "date-fns";
import type {Locale} from "date-fns/locale";
import {
    dibAdminApi,
    type PublicNewsArticleV2,
} from "../../dibAdminApi/dibAdminApi";
import {ExperimentalLink} from "../../components/ExperimentalLink";
import IconX from "../../assets/x.svg?react";
import {useNewsFilters} from "./useNewsFilters";
import {useNewsGroupings} from "./useNewsGroupings";
import {bffApi} from "../chat/bffApi";
import classes from "./NewsPage.module.css";
import {getDateLocale} from "../../utils/dateLocales";

const PAGE_SIZE = 50;
// The news endpoint caps take at 500 server-side; stop offering "show more"
// once the merged list reaches it.
const SERVER_TAKE_CAP = 500;

type NewsItemProps = {
    item: PublicNewsArticleV2;
    locale: Locale;
    onSourceClick: (source: string) => void;
};

function NewsItem({item, locale, onSourceClick}: NewsItemProps) {
    const date = item.pubDate ? new Date(item.pubDate) : null;
    const source = item.source;
    const [trackOpened] = bffApi.endpoints.trackOpened.useMutation();

    const title = item.link ? (
        <ExperimentalLink
            href={item.link}
            className={classes.itemTitle}
            onClick={() =>
                trackOpened({
                    entity: "news",
                    key: item.topicId || item.link || undefined,
                    source: "news",
                })
            }
        >
            {item.title}
        </ExperimentalLink>
    ) : (
        <span className={classes.itemTitle}>{item.title}</span>
    );

    return (
        <div className={classes.item}>
            <div className={classes.itemDate}>
                {date && (
                    <>
                        <span className={classes.itemDay}>
                            {format(date, "dd")}
                        </span>
                        <span className={classes.itemMonth}>
                            {/* nb/da abbreviate with a trailing period ("aug."),
                                which reads as a typo once uppercased. */}
                            {format(date, "MMM", {locale}).replace(/\.$/, "")}
                        </span>
                    </>
                )}
            </div>
            <div className={classes.itemBody}>
                <span className={classes.itemTitleRow}>{title}</span>
                {item.description && (
                    <Text className={classes.itemDescription}>
                        {item.description}
                    </Text>
                )}
                {source && (
                    <UnstyledButton
                        className={classes.sourcePill}
                        onClick={() => onSourceClick(source)}
                    >
                        {source}
                    </UnstyledButton>
                )}
            </div>
        </div>
    );
}

function ListSkeleton() {
    return (
        <Stack gap="lg" className={classes.list}>
            {Array.from({length: 6}, (_, i) => (
                <Skeleton key={i} height={48} />
            ))}
        </Stack>
    );
}

type NewsListProps = {
    databaseIds: number[];
    source: string | null;
    onSourceClick: (source: string) => void;
};

// Holds the paging state; remounted (via key) when filters change so `skip`
// resets in the same render as the new filter args.
function NewsList({databaseIds, source, onSourceClick}: NewsListProps) {
    const {t, i18n} = useTranslation();
    const [skip, setSkip] = useState(0);
    const {data, isLoading, isFetching, isError} =
        dibAdminApi.endpoints.getNews.useQuery({
            databaseIds,
            sources: source ? [source] : [],
            skip,
            take: PAGE_SIZE,
        });

    const locale = getDateLocale(
        i18n.resolvedLanguage ?? i18n.language ?? "nb"
    );

    if (isLoading) return <ListSkeleton />;

    if (isError) {
        return (
            <Alert color="red" title="Error">
                {t("News.loadError", {defaultValue: "Failed to load news."})}
            </Alert>
        );
    }

    if (!data || data.items.length === 0) {
        return (
            <Text className={classes.empty}>
                {t("News.empty", {defaultValue: "No news found"})}
            </Text>
        );
    }

    const hasMore = data.items.length < Math.min(data.total, SERVER_TAKE_CAP);

    return (
        <div className={classes.list}>
            {data.items.map((item) => (
                <NewsItem
                    key={item.topicId}
                    item={item}
                    locale={locale}
                    onSourceClick={onSourceClick}
                />
            ))}
            {hasMore && (
                <UnstyledButton
                    className={classes.loadMore}
                    disabled={isFetching}
                    onClick={() => setSkip(data.items.length)}
                >
                    {t("Phrase.ShowMore")}
                </UnstyledButton>
            )}
        </div>
    );
}

export function NewsPage() {
    const {t} = useTranslation();
    const isMobile = useMediaQuery("(max-width: 48em)");
    const {source, setValue, toggleValue} = useNewsFilters();
    const groupings = useNewsGroupings();

    const filterKey = JSON.stringify({
        databaseIds: groupings.databaseIds,
        source,
    });

    return (
        <Stack gap="md" className={classes.root}>
            {/* Desktop area filter lives in NewsFilterSidebar (DashboardLayout
                slot); mobile gets the same single-select as a dropdown. */}
            {isMobile && (
                <Select
                    data={groupings.options.map((o) => ({
                        value: o.slug,
                        label: o.name,
                        disabled: o.disabled,
                    }))}
                    value={
                        groupings.options.some(
                            (o) => o.slug === groupings.selectedSlug
                        )
                            ? groupings.selectedSlug
                            : null
                    }
                    onChange={(value) => setValue("groupings", value)}
                    placeholder={t("News.allAreas", {
                        defaultValue: "All areas",
                    })}
                    clearable
                />
            )}
            {source && (
                <div className={classes.activeFilters}>
                    <UnstyledButton
                        className={classes.sourceChip}
                        onClick={() => setValue("sources", null)}
                    >
                        {source}
                        <span className={classes.sourceChipRemove} aria-hidden>
                            <IconX />
                        </span>
                    </UnstyledButton>
                </div>
            )}
            {/* On a deep-link with an area selected, wait for the slug →
                databaseIds mapping before querying, or the first request
                would fetch unfiltered. */}
            {groupings.isLoading && groupings.selectedSlug ? (
                <ListSkeleton />
            ) : (
                <NewsList
                    key={filterKey}
                    databaseIds={groupings.databaseIds}
                    source={source}
                    onSourceClick={(value) => toggleValue("sources", value)}
                />
            )}
        </Stack>
    );
}
